<template>
  <div class="auth">
    <back-home-btn></back-home-btn>

    <v-container class="pa-3 pt-5">
      <div class="text-body-2 mb-3">マイアカウント</div>

      <v-card color="white" class="mb-3">
        <v-card-title>
          基本情報
        </v-card-title>
        <v-card-text>
          <v-divider class="mb-5"></v-divider>

          <!-- 氏名、グループ名 -->
          <v-row no-gutters>
            <template v-if="authUser && authUser.authority == 0">
              <v-col cols="12">
                <v-form class="px-3" ref="nameForm">
                  <!-- 氏名 -->
                  <v-text-field
                    v-model="name"
                    prepend-icon="mdi-account"
                    :rules="[rules.required, rules.max]"
                    counter
                    maxlength="30"
                    label="氏名"
                  ></v-text-field>
                  <div>
                    {{ nameErrorMessage }}
                  </div>
                </v-form>
              </v-col>
              <v-col cols="12" class="text-end">
                <v-btn
                  text
                  color="primary"
                  :loading="saveNameLoading"
                  @click="saveName"
                  >変更を保存する</v-btn
                >
              </v-col>
            </template>
            <template v-else-if="authUser && authUser.authority == 1">
              <v-col>
                <div class="pb-5">グループ名： {{ authUser.name }}</div>
              </v-col>
            </template>
            <template v-else>
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </template>
          </v-row>

          <!-- メールアドレス -->
          <v-row no-gutters>
            <template v-if="authUser">
              <v-col cols="12">
                <v-form class="px-3" ref="emailForm">
                  <!-- メールアドレス  -->
                  <v-text-field
                    v-model="email"
                    prepend-icon="mdi-email"
                    :rules="[rules.required, rules.email]"
                    label="メールアドレス"
                  ></v-text-field>
                  <div>
                    {{ emailErrorMessage }}
                  </div>
                </v-form>
              </v-col>
              <v-col cols="12" class="text-end">
                <v-btn
                  text
                  color="primary"
                  :loading="saveEmailLoading"
                  @click="saveEmail"
                  >変更を保存する</v-btn
                >
              </v-col>
            </template>
            <template v-else>
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </template>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card color="white" class="mb-3">
        <v-card-title>
          パスワード変更
        </v-card-title>
        <v-card-text>
          <v-divider class="mb-5"></v-divider>
          <v-form class="px-3" ref="passwordForm">
            <!-- 現在のパスワード -->
            <v-text-field
              v-model="currentPassword"
              prepend-icon="mdi-lock"
              :rules="[rules.required, rules.password]"
              type="password"
              label="現在のパスワード"
              counter
            ></v-text-field>
            <!-- 新しいパスワード -->
            <v-text-field
              v-model="newPassword"
              prepend-icon="mdi-lock"
              :rules="[rules.required, rules.password]"
              type="password"
              label="新しいパスワード"
              counter
            ></v-text-field>
            <!-- 新しいパスワード（確認） -->
            <v-text-field
              v-model="confirmPassword"
              prepend-icon="mdi-lock"
              :rules="[rules.required, rules.password]"
              type="password"
              label="新しいパスワード（確認）"
              counter
            ></v-text-field>
          </v-form>
          <div>
            {{ passwordErrorMessage }}
          </div>
        </v-card-text>
        <v-card-actions class="pa-5 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="savePassword"
            :loading="savePasswordLoading"
          >
            <span>変更を保存する</span>
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card color="white" class="mb-3">
        <v-card-title>
          アカウント削除
        </v-card-title>
        <template v-if="authUser && authUser.authority == 0">
          <v-card-text>
            <v-divider class="mb-5"></v-divider>
            「余韻の米蔵」の退会処理を行います。過去の注文履歴などは全て削除され、復旧はできません。
          </v-card-text>
          <v-card-actions class="pa-5 pt-0">
            <v-spacer></v-spacer>
            <v-btn color="error" @click="beforeDelete">
              <span>アカウントを削除する</span>
            </v-btn>
          </v-card-actions>
        </template>
        <template v-else>
          <v-card-text>
            <v-divider class="mb-5"></v-divider>
            <div>グループ代表ユーザーのため削除はできません。</div>
            <div>
              グループ契約の解除については今井農園までお問い合わせください。
            </div>
          </v-card-text>
        </template>
      </v-card>
    </v-container>

    <!-- 削除確認ダイアログ -->
    <continue-dialog
      ref="coninueDialog"
      title="確認"
      continue-text="削除する"
      continue-color="error"
      :execute-action="deleteAccount"
    >
      本当にアカウントを削除しますか？<br />
    </continue-dialog>

    <!-- 確定時メッセージ -->
    <v-snackbar v-model="snackbar" :timeout="5000" bottom color="info" dark>
      <template v-if="confirmMessage">
        {{ confirmMessage }}
      </template>
      <template v-else>
        入力したメールアドレス宛にメールを送信しました。<br />
        メールに記載されたリンクから処理を続行してください。
      </template>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          閉じる
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as authApi from '../api/modules/auth';
import { HttpStatuses } from '../constants/komegura-config';
import { isSuccessStatus } from '../utils/http';
import { isValidEmail } from '../utils/validators';
import ContinueDialog from '../components/feature/ContinueDialog.vue';
import BackHomeBtn from '../components/common/BackHomeBtn.vue';

export default {
  components: {
    ContinueDialog,
    BackHomeBtn,
  },
  data: () => ({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    saveNameLoading: false,
    saveEmailLoading: false,
    savePasswordLoading: false,
    snackbar: false,
    confirmMessage: '',
    rules: {
      required: value => !!value || '必須入力です',
      max: v => v.length <= 30 || '30文字以内で入力してください',
      email: value => isValidEmail(value) || '無効なメールアドレスです',
      password: value => {
        const pattern = /^(?=.*?[a-z])(?=.*?\\d)[a-z\\d]{6,100}$/i;
        return (
          pattern.test(value) ||
          '半角英数字をそれぞれ1種類以上含んだ6文字以上で入力してください'
        );
      },
    },
    nameErrorMessage: null,
    emailErrorMessage: null,
    passwordErrorMessage: null,
  }),
  computed: {
    ...mapGetters({
      // 認証ユーザー
      authUser: 'auth/authUser',
      accessToken: 'auth/accessToken',
    }),
  },
  methods: {
    async saveName() {
      if (!this.$refs.nameForm.validate()) {
        return;
      }
      this.saveNameLoading = true;
      this.nameErrorMessage = null;
      const result = await authApi.updateCustomer({
        id: this.authUser.id,
        params: { name: this.name },
        accessToken: this.accessToken,
      });

      if (isSuccessStatus(result.statusCode)) {
        this.$store.dispatch('auth/setAuthUser', this.accessToken);
        this.confirmMessage = '保存しました。';
        this.snackbar = true;
      } else {
        this.$store.dispatch('error/setError', {
          code: result.statusCode,
          id: result.requestId,
          message: result.response,
        });
      }
      this.saveNameLoading = false;
    },
    async saveEmail() {
      if (!this.$refs.emailForm.validate()) {
        return;
      }
      this.saveEmailLoading = true;
      this.emailErrorMessage = null;

      const result = await authApi.updateCustomerEmail({
        customerId: this.authUser.id,
        email: this.email,
        accessToken: this.accessToken,
      });

      if (isSuccessStatus(result.statusCode)) {
        this.confirmMessage = null;
        this.snackbar = true;
      } else if (result.statusCode === HttpStatuses.CONFLICT) {
        this.emailErrorMessage = '既に使用されているメールアドレスです。';
      } else {
        this.$store.dispatch('error/setError', {
          code: result.statusCode,
          id: result.requestId,
          message: result.response,
        });
      }
      this.saveEmailLoading = false;
    },
    async savePassword() {
      if (!this.$refs.passwordForm.validate()) {
        return;
      }
      if (this.newPassword != this.confirmPassword) {
        this.passwordErrorMessage = '新しいパスワードが一致しません。';
        return;
      }
      this.savePasswordLoading = true;
      this.passwordErrorMessage = null;

      const result = await authApi.updateCustomer({
        id: this.authUser.id,
        params: {
          current_password: this.currentPassword,
          password: this.newPassword,
        },
        accessToken: this.accessToken,
      });

      if (isSuccessStatus(result.statusCode)) {
        this.confirmMessage = '保存しました。';
        this.snackbar = true;
      } else if (result.statusCode === HttpStatuses.UNAUTHORIZED) {
        this.passwordErrorMessage = '現在のパスワードが違います。';
      } else {
        this.$store.dispatch('error/setError', {
          code: result.statusCode,
          id: result.requestId,
          message: result.response,
        });
      }
      this.savePasswordLoading = false;
    },
    beforeDelete() {
      this.$refs.coninueDialog.open();
    },
    async deleteAccount() {
      const result = await authApi.deleteCustomer({
        id: this.authUser.id,
        accessToken: this.accessToken,
      });
      if (isSuccessStatus(result.statusCode)) {
        this.$store.dispatch('auth/clearOrderHistories');
        this.$store.dispatch('auth/clearAddressLists');
        this.$store.dispatch('cart/clearCart');
        this.$store.dispatch('merchandise/resetItemDetail');
        await this.$store.dispatch('auth/logout');
        this.$store.dispatch('error/clearError');
        window.location = '/';
      } else {
        this.$store.dispatch('error/setError', {
          code: result.statusCode,
          id: result.requestId,
          message: result.response,
        });
      }
    },
  },
  created() {
    if (this.authUser) {
      this.name = this.authUser.name;
      this.email = this.authUser.email;
    }
  },
  watch: {
    authUser: function() {
      if (this.authUser) {
        this.name = this.authUser.name;
        this.email = this.authUser.email;
      }
    },
  },
};
</script>
